// Webhooks: Hilfen ohne React (Labels, Validierung, Fehlertexte, Beispiele).
import { isTextBased, OUTGOING_WEBHOOK_EVENTS, type ChannelDto, type OutgoingWebhookEvent, type WebhookDto } from '@lagerfeuer/shared';
import { ApiError } from '../../../lib/api';
import { absoluteUrl, errorMessage } from '../format';

export const WEBHOOK_EVENT_LABELS: Record<OutgoingWebhookEvent, string> = {
  MESSAGE_CREATE: 'Nachricht gesendet',
  MESSAGE_UPDATE: 'Nachricht bearbeitet',
  MESSAGE_DELETE: 'Nachricht gelöscht',
  MESSAGE_REACTION_ADD: 'Reaktion hinzugefügt',
  MESSAGE_REACTION_REMOVE: 'Reaktion entfernt',
  SERVER_MEMBER_ADD: 'Mitglied beigetreten',
  SERVER_MEMBER_REMOVE: 'Mitglied entfernt/gegangen',
  CHANNEL_CREATE: 'Kanal erstellt',
  CHANNEL_UPDATE: 'Kanal geändert',
  CHANNEL_DELETE: 'Kanal gelöscht',
  THREAD_CREATE: 'Thread erstellt',
};
export const ALL_WEBHOOK_EVENTS: readonly OutgoingWebhookEvent[] = OUTGOING_WEBHOOK_EVENTS;

export const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const;
export const IMAGE_ACCEPT = IMAGE_TYPES.join(',');
export const isAllowedImage = (f: File) => (IMAGE_TYPES as readonly string[]).includes(f.type);

/** Kanäle, in die ein eingehender Webhook posten kann (textbasiert, keine Kategorie/Threads/DMs). */
export function webhookTargetChannels(channels: readonly ChannelDto[] | undefined): ChannelDto[] {
  return (channels ?? [])
    .filter((c) => c.type !== 'CATEGORY' && isTextBased(c.type) && !!c.serverId && !c.type.includes('THREAD') && !c.type.includes('DM'))
    .sort((a, b) => a.position - b.position);
}

export function channelLabel(c: ChannelDto | undefined): string {
  if (!c) return 'unbekannter Kanal';
  return `${c.type === 'VOICE' ? '🔊 ' : '#'}${c.name ?? c.id}`;
}

/** Nur https:// mit Host akzeptieren (die eigentliche SSRF-Prüfung macht der Server). */
export function validateHttpsUrl(url: string): string | null {
  const v = url.trim();
  if (!v) return 'Bitte eine Ziel-URL angeben.';
  let u: URL;
  try {
    u = new URL(v);
  } catch {
    return 'Ungültige URL.';
  }
  if (u.protocol !== 'https:') return 'Nur https://-Adressen sind erlaubt.';
  if (!u.hostname) return 'Ungültige URL.';
  return null;
}

/** Fertige, absolute Ausführungs-URL eines frisch erzeugten Webhooks. */
export function webhookExecUrl(w: WebhookDto): string | null {
  if (w.url) return absoluteUrl(w.url);
  if (w.token) return absoluteUrl(`/api/webhooks/${w.id}/${w.token}`);
  return null;
}

export function curlExample(url: string): string {
  return `curl -H 'Content-Type: application/json' -d '{"content":"Hallo"}' ${url}`;
}

export const SIGNATURE_EXAMPLE = `X-Lagerfeuer-Signature: sha256=HMAC_SHA256(secret, timestamp + "." + body)`;

/** Fehlende Rechte (403) bzw. versteckte Ressource (404) → verständliche Meldung. */
export function webhooksErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'LIMIT_REACHED') return 'In diesem Kanal gibt es schon die maximale Anzahl an Webhooks.';
    if (err.status === 403 || err.status === 404) {
      return 'Dir fehlt das Recht „Webhooks verwalten" in diesem Server (oder der Eintrag existiert nicht mehr).';
    }
  }
  return errorMessage(err);
}

/** React-Query-Schlüssel. */
export const incomingKey = (serverId: string) => ['extras', 'webhooks', serverId] as const;
export const outgoingKey = (serverId: string) => ['extras', 'outgoing-webhooks', serverId] as const;
export const deliveriesKey = (id: string) => ['extras', 'outgoing-deliveries', id] as const;
export const channelsKey = (serverId: string) => ['extras', 'lookup', 'channels', serverId] as const;
export const DELIVERY_PAGE = 25;
